// services/booksApi.ts — HTTP client for the bookshelf endpoints.
//
// Two routes today:
//   GET  /api/stories/:id/chapters       — list chapters for a story
//   POST /api/stories/:id/backfill-book  — synchronous backfill (rare;
//                                           the server auto-triggers on
//                                           the first turn against a
//                                           legacy story).
//
// Both bearer-authed via authedFetch.

import { authedFetch } from "../auth/tokens";
import type { SequelOption } from "../engine";

export interface ChapterDto {
  id: string;
  chapterNumber: number;
  title: string;
  bodyMd: string;
  startTurn: number;
  endTurn: number;
  /** "ready" | "generating" | "skipped" | "failed" — Phase 1 only emits "ready". */
  status: string;
  generatedAt: string;
}

export interface ChaptersResponse {
  chapters: ChapterDto[];
}

export interface BackfillResponse {
  ok: boolean;
  chaptersGenerated: number;
  totalCostUsd?: number;
  reason?: string;
}

export class BooksApiError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "BooksApiError";
    this.status = status;
    this.code = code;
  }
}

async function parseOrThrow<T>(res: Response): Promise<T> {
  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // Non-JSON body — fall through.
    }
  }
  if (!res.ok) {
    const errBody = (body as { error?: string; code?: string }) || {};
    const msg =
      errBody.error ||
      (text ? text.slice(0, 200) : `Request failed with status ${res.status}`);
    throw new BooksApiError(msg, res.status, errBody.code);
  }
  return body as T;
}

/**
 * GET /api/stories/:id/chapters — list a story's chapters. Read-only;
 * unaffected by the forever-default lock.
 */
export async function chaptersQuery(
  storyId: string,
): Promise<ChaptersResponse> {
  const res = await authedFetch(
    `/api/stories/${encodeURIComponent(storyId)}/chapters`,
    { method: "GET" },
  );
  return parseOrThrow<ChaptersResponse>(res);
}

/**
 * POST /api/stories/:id/backfill-book — trigger the synchronous backfill.
 * Server is idempotent — `chaptersGenerated: 0, reason: "already_has_chapters"`
 * when nothing happens. Phase 1 callers typically don't need this; the
 * server auto-triggers on the first turn against a legacy story.
 */
export async function backfillBook(
  storyId: string,
): Promise<BackfillResponse> {
  const res = await authedFetch(
    `/api/stories/${encodeURIComponent(storyId)}/backfill-book`,
    { method: "POST" },
  );
  return parseOrThrow<BackfillResponse>(res);
}

// ── Author-private fork ("continue from a point") ──────────────────────

export interface ForkOwnStoryResponse {
  newStoryId: string;
}

/**
 * POST /api/stories/:id/fork — fork the CALLER'S OWN story at the end of
 * `forkChapterNumber` into a NEW private continuation. Does NOT publish
 * anything — no public slug is created. Owner-only on the server (404 if
 * the story isn't yours). Returns the new forked story's id.
 */
export async function forkOwnStory(
  storyId: string,
  forkChapterNumber: number,
): Promise<ForkOwnStoryResponse> {
  const res = await authedFetch(
    `/api/stories/${encodeURIComponent(storyId)}/fork`,
    {
      method: "POST",
      body: JSON.stringify({ forkChapterNumber }),
    },
  );
  return parseOrThrow<ForkOwnStoryResponse>(res);
}

// ── Phase 3b series / "next book" ──────────────────────────────────────

export interface BeginSequelResponse {
  newStoryId: string;
}

/**
 * POST /api/stories/:id/sequel — spawn Book N+1 of a series from the CALLER'S
 * OWN finished story, using a chosen SequelOption. Owner-only + finished-only
 * on the server (404 if not yours, 409 with code "not_finished" if the source
 * isn't finished). Returns the new book's id; switch into it via the same
 * activate path the fork uses.
 */
export async function beginSequel(
  storyId: string,
  option: SequelOption,
): Promise<BeginSequelResponse> {
  const res = await authedFetch(
    `/api/stories/${encodeURIComponent(storyId)}/sequel`,
    {
      method: "POST",
      body: JSON.stringify({ option }),
    },
  );
  return parseOrThrow<BeginSequelResponse>(res);
}

/**
 * GET /api/stories/:id/sequel-options — fetch the persisted "next book" slate
 * for a finished story (Phase 3b). Owner-only (404 if not yours). Returns the
 * persisted SequelOption[] (empty when the column is null). Used when a player
 * reopens a finale they earned in an earlier session — the winning-turn
 * response that originally carried the slate is gone, so we read it back.
 */
export async function getSequelOptions(
  storyId: string,
): Promise<SequelOption[]> {
  const res = await authedFetch(
    `/api/stories/${encodeURIComponent(storyId)}/sequel-options`,
    { method: "GET" },
  );
  const body = await parseOrThrow<{ sequelOptions?: unknown }>(res);
  return Array.isArray(body.sequelOptions)
    ? (body.sequelOptions as SequelOption[])
    : [];
}

// ── Phase 2 share endpoints ────────────────────────────────────────────

export type ShareStatus = "shared" | "listed" | "revoked";

export interface ShareResponse {
  slug: string;
  url: string;
  isNew?: boolean;
  allowContinuation: boolean;
  allowEpub: boolean;
  customSubtitle: string | null;
  status: ShareStatus;
}

/**
 * POST /api/stories/:id/share — publish (idempotent). Returns the slug
 * and canonical share URL. Re-clicking just returns the existing slug.
 */
export async function shareStory(storyId: string): Promise<ShareResponse> {
  const res = await authedFetch(
    `/api/stories/${encodeURIComponent(storyId)}/share`,
    { method: "POST" },
  );
  return parseOrThrow<ShareResponse>(res);
}

export interface UpdateShareSettingsInput {
  allowContinuation?: boolean;
  allowEpub?: boolean;
  customSubtitle?: string | null;
  status?: ShareStatus;
}

/**
 * PUT /api/stories/:id/share — update toggles / subtitle / status.
 * Returns the updated row.
 */
export async function updateShareSettings(
  storyId: string,
  input: UpdateShareSettingsInput,
): Promise<ShareResponse> {
  const res = await authedFetch(
    `/api/stories/${encodeURIComponent(storyId)}/share`,
    {
      method: "PUT",
      body: JSON.stringify(input),
    },
  );
  return parseOrThrow<ShareResponse>(res);
}

/**
 * DELETE /api/stories/:id/share — revoke. The slug 404s afterwards. The
 * author can re-share via PUT { status: "shared" } to revive.
 */
export async function revokeShare(storyId: string): Promise<{ ok: boolean }> {
  const res = await authedFetch(
    `/api/stories/${encodeURIComponent(storyId)}/share`,
    { method: "DELETE" },
  );
  return parseOrThrow<{ ok: boolean }>(res);
}

// ── Phase 2b share artifacts (AI-generated share copy) ─────────────────

export interface ShareArtifactsResponse {
  /** Short, hooky, spoiler-free back-cover line. */
  blurb: string;
  /** Spoiler-free synopsis paragraph. */
  summary: string;
  /** Short in-voice / second-person framing. */
  foreword: string;
  /** A vivid non-finale passage that hooks. */
  excerpt: string;
  /** ISO 8601 timestamp of generation. */
  generatedAt: string;
  /** Model string used to generate. */
  model: string;
  /** True when the story is published + publicly readable (a public URL exists). */
  published: boolean;
  /** Canonical public reader URL, or null when not published. */
  url: string | null;
}

/**
 * GET /api/stories/:id/share-artifacts — owner-only. Returns ready-to-paste
 * share copy generated from the book's chapters (cached server-side once a
 * published_books row exists). Throws BooksApiError with code "no_chapters"
 * (409) when the book hasn't been chaptered yet; other generation failures
 * surface as 502.
 */
export async function getShareArtifacts(
  storyId: string,
): Promise<ShareArtifactsResponse> {
  const res = await authedFetch(
    `/api/stories/${encodeURIComponent(storyId)}/share-artifacts`,
    { method: "GET" },
  );
  return parseOrThrow<ShareArtifactsResponse>(res);
}
